import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { isNavigableUrl } from '../../utils/external-link';

/** Un onglet ouvert pendant le clic, dont la destination n'est connue qu'après. */
export interface ReservedTab {
  /** Y charge `url` ; refuse, et referme l'onglet, ce qui n'est pas `http(s)`. */
  navigate(url: string | null | undefined): boolean;
  close(): void;
}

/**
 * Le chemin des adresses externes venues des données — lien de visio, lien
 * d'un support de cours — quand c'est le code qui navigue.
 *
 * <p>Chaque sortie vérifie d'abord le schéma (`isNavigableUrl`) et renvoie
 * `false` sans rien ouvrir quand il n'est pas `http(s)` : à l'écran de dire ce
 * que cela signifie pour lui. Un `window.open` posé dans chaque composant
 * oubliait ce contrôle à un endroit sur deux. Un `[href]` lié dans un gabarit
 * n'en a pas besoin : Angular le désinfecte, et `javascript:` y devient
 * `unsafe:javascript:` ; `window.open` et `location`, eux, ne sont pas
 * désinfectés.</p>
 *
 * <p>Un service plutôt qu'un appel direct à `window` : la navigation de
 * premier niveau décharge la page, donc le test Karma qui la déclencherait
 * quitterait sa propre suite. Derrière une injection, elle se remplace par un
 * espion et l'URL rendue se vérifie.</p>
 */
@Injectable({ providedIn: 'root' })
export class ExternalNavigationService {
  private readonly document = inject(DOCUMENT);

  /**
   * Ouvre `url` dans un nouvel onglet, sans référence en retour : la page
   * ouverte ne peut ni rediriger l'application (`noopener`), ni apprendre
   * d'où vient l'élève (`noreferrer`).
   */
  open(url: string | null | undefined): boolean {
    if (!isNavigableUrl(url)) return false;
    this.document.defaultView?.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }

  /**
   * Remplace la page courante par `url` — une entrée dans l'historique, comme
   * un lien. C'est le cas de la page d'atterrissage du mail de rappel, où
   * l'utilisateur vient d'arriver pour être redirigé : un second onglet le
   * laisserait sur une page vide.
   */
  assign(url: string | null | undefined): boolean {
    if (!isNavigableUrl(url)) return false;
    this.document.defaultView?.location.assign(url);
    return true;
  }

  /**
   * Ouvre un onglet vide tant qu'on est encore dans le clic : ouvert après une
   * réponse réseau, il serait bloqué comme fenêtre surgissante.
   *
   * <p>`noopener` n'est pas possible ici — il rendrait `null` au lieu de
   * l'onglet, qu'on ne pourrait plus diriger. Le lien en retour est donc coupé
   * à la main, avant toute navigation : l'onglet `about:blank` hérite de
   * l'origine de l'application, et la page qui s'y chargera ne doit pas
   * pouvoir la rediriger par `window.opener`.</p>
   */
  reserveTab(): ReservedTab {
    const tab = this.document.defaultView?.open('about:blank', '_blank') ?? null;
    if (tab) tab.opener = null;

    return {
      navigate: (url) => {
        if (!isNavigableUrl(url)) {
          tab?.close();
          return false;
        }
        if (tab) {
          tab.location.href = url;
          return true;
        }
        return this.open(url);
      },
      close: () => tab?.close(),
    };
  }
}
